import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  const handleSessionExpired = useCallback(() => {
    console.log('Session expired event received');
    setUser(null);
    setSessionError('Your session has expired. Please login again.');
    localStorage.removeItem('token');
  }, []);

  useEffect(() => {
    // Listen for session expired events from API interceptor
    window.addEventListener('session-expired', handleSessionExpired);
    
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [handleSessionExpired]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setSessionError(null);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only remove token if it's actually an auth error
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      }
      // For network errors, keep the token and retry later
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setSessionError(null);
  };

  const register = async (name, username, email, password, whatsapp_number, referral_username, pin = null) => {
    const response = await api.post('/auth/register', { 
      name, 
      username, 
      email, 
      password, 
      whatsapp_number, 
      referral_username: referral_username || null,
      pin: pin || null
    });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    setSessionError(null);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setSessionError(null);
  };

  // Refresh user data periodically (every 5 minutes) to keep session alive
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      fetchUser();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    fetchUser,
    isAdmin: user?.role === 'admin',
    sessionError,
    clearSessionError: () => setSessionError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
